import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { api, getToken, syncIdentity } from './lib/api';
import { connectWs, disconnectWs } from './lib/ws';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import WebclientLayout from './webclient/WebclientLayout';
import Chat from './webclient/Chat';
import WebclientCalls from './webclient/Calls';
import WebclientVoicemail from './webclient/Voicemail';
import WebclientContacts from './webclient/Contacts';
import WebclientSettings from './webclient/Settings';
import Placeholder from './webclient/Placeholder';
import { isAdminRole } from './webclient/role';
import Users from './pages/Users';
import UserEditor from './pages/UserEditor';
import ForwardingProfiles from './pages/ForwardingProfiles';
import Phones from './pages/Phones';
import PnpPhones from './pages/PnpPhones';
import HotDeskingPhones from './pages/HotDeskingPhones';
import PhoneFirmwares from './pages/PhoneFirmwares';
import OutboundRules from './pages/OutboundRules';
import OutboundRulesV2 from './pages/OutboundRulesV2';
import CallLegs from './pages/CallLegs';
import WakeupCalls from './pages/WakeupCalls';
import RoomStatus from './pages/RoomStatus';
import LdapSync from './pages/LdapSync';
import PublicTalk from './pages/PublicTalk';
import E911Locations from './pages/E911Locations';
import VoicemailAI from './pages/VoicemailAI';
import PushDevices from './pages/PushDevices';
import FirmwareHistory from './pages/FirmwareHistory';
import AdminAlerts from './pages/AdminAlerts';
import BossSecretary from './pages/BossSecretary';
import PickupGroups from './pages/PickupGroups';
import RingGroups from './pages/RingGroups';
import Health from './pages/Health';
import { IPBlacklist, AntiHacking, ConsoleRestrictions, NetworkSettings, AllowedCountries, CIDRules, Codecs, Parameters, SystemPrompts, XMLTemplates, E164, LicensePage, UpdatesPage, MultiCompany } from './pages/AdvancedPages';
import Backup from './pages/Backup';
import ThemeStyle from './pages/ThemeStyle';
import OfficeHours from './pages/OfficeHours';
import CallRecordings from './pages/CallRecordings';
import Firewall from './pages/Firewall';
import Services from './pages/Services';
import Reports from './pages/Reports';
import Voicemail from './pages/Voicemail';
import SharedVoicemail from './pages/SharedVoicemail';
import Wallboard from './pages/Wallboard';
import Audit from './pages/Audit';
import EventLog from './pages/EventLog';
import Integrations from './pages/Integrations';
import Advanced from './pages/Advanced';
import SystemPages from './pages/SystemPages';
import Billing from './pages/Billing';
import Reseller from './pages/Reseller';
import Apps from './pages/Apps';
import BetaTesters from './pages/BetaTesters';
import Import3cx from './pages/Import3cx';
import Meet from './pages/Meet';
import CallFlow from './pages/CallFlow';
import PhoneTemplates from './pages/PhoneTemplates';
import CallFlowDesigner from './pages/CallFlowDesigner';
import CrmTemplateEditor from './pages/CrmTemplateEditor';
import CrmWriteback from './pages/CrmWriteback';
import DectFxs from './pages/DectFxs';
import Fax from './pages/Fax';
import InboundRules from './pages/InboundRules';
import Holidays from './pages/Holidays';
import ServicePrincipals from './pages/ServicePrincipals';
import EmergencyGeo from './pages/EmergencyGeo';
import TrunkTemplates from './pages/TrunkTemplates';
import ActivityLog from './pages/ActivityLog';
import Blocklist from './pages/Blocklist';
import IpAllowlist from './pages/IpAllowlist';
import Contacts from './pages/Contacts';
import ApiKeys from './pages/ApiKeys';
import SbcOnboarding from './pages/SbcOnboarding';
import Settings from './pages/Settings';
import AutoProvisioning from './pages/AutoProvisioning';
import VoiceChat from './pages/VoiceChat';
import AddTrunk from './pages/AddTrunk';
import StirShaken from './pages/StirShaken';
import RingGroupEditor from './pages/RingGroupEditor';
import PagingGroups from './pages/PagingGroups';
import QueueEditor from './pages/QueueEditor';
import DigitalReceptionistEditor from './pages/DigitalReceptionistEditor';
import Hotdesk from './pages/Hotdesk';
import Departments from './pages/Departments';
import Groups from './pages/Groups';
import AiAgents from './pages/AiAgents';
import SkillRouting from './pages/SkillRouting';
import CdrAnomaly from './pages/CdrAnomaly';
import RemoteVoicemail from './pages/RemoteVoicemail';
import WebPhone from './pages/WebPhone';
import ConferenceRooms from './pages/ConferenceRooms';
import { ToastProvider } from './components/Toast';

function RequireAuth({ children }: { children: JSX.Element }) {
  const me = api.me();
  if (!me) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  useEffect(() => {
    if (getToken()) connectWs();
    return () => disconnectWs();
  }, []);

  // Pick up an admin's role change (promote/demote) without a full re-login: on load, re-mint the
  // token from the server (which re-reads console_role) and reload once if the role changed. (#466)
  useEffect(() => { void syncIdentity(); }, []);

  // Apply the admin-configured favicon AND UI theme portal-wide (and re-apply when
  // changed via Theme & Style, which dispatches 'stratus-theme-changed').
  useEffect(() => {
    const apply = async () => {
      try {
        const r = await fetch('/api/theme/public/config'); if (!r.ok) return;
        const c = await r.json();
        if (c.favicon) {
          let link = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
          if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
          link.removeAttribute('type');
          link.href = `/api/theme/public/favicon?v=${c.favicon_v || 0}`;
        }
        const t = c.theme || 'beacon';   // Beacon is the default system theme
        const el = document.documentElement;
        // NB: the admin theme lives on data-stratus-theme and is server-driven — do NOT
        // persist it to localStorage['stratus-theme'], which is owned by the light/dark
        // toggle (lib/theme.ts). Writing here clobbered that key and broke the toggle.
        if (t && t !== 'default') el.setAttribute('data-stratus-theme', t);
        else el.removeAttribute('data-stratus-theme');
      } catch { /* keep static defaults */ }
    };
    apply();
    window.addEventListener('stratus-theme-changed', apply);
    return () => window.removeEventListener('stratus-theme-changed', apply);
  }, []);

  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      {/* User webclient — the 3CX-style second surface for end users. */}
      <Route path="/webclient" element={<RequireAuth><WebclientLayout /></RequireAuth>}>
        <Route index element={<Navigate to="chat" replace />} />
        <Route path="chat" element={<Chat />} />
        <Route path="calls" element={<WebclientCalls />} />
        <Route path="contacts" element={<WebclientContacts />} />
        <Route path="voicemail" element={<WebclientVoicemail />} />
        <Route path="meet" element={<Placeholder title="Meet" note="Video meetings land here soon." />} />
        <Route path="settings" element={<WebclientSettings />} />
      </Route>

      <Route path="/" element={<RequireAuth><Layout /></RequireAuth>}>
        {/* Non-admin users have no business in the admin console — send them to their webclient. */}
        <Route index element={isAdminRole(api.me()) ? <Dashboard /> : <Navigate to="/webclient" replace />} />
        <Route path="users" element={<Users />} />
        <Route path="users/new" element={<UserEditor />} />
        <Route path="users/:extension_uuid/edit" element={<UserEditor />} />
        <Route path="forwarding-profiles/:extension_uuid" element={<ForwardingProfiles />} />
        <Route path="phones" element={<Phones />} />
        <Route path="phones/pnp" element={<PnpPhones />} />
        <Route path="phones/hot-desking" element={<HotDeskingPhones />} />
        <Route path="phones/firmwares" element={<PhoneFirmwares />} />
        <Route path="voice-chat" element={<VoiceChat />} />
        <Route path="webphone" element={<WebPhone />} />
        <Route path="settings" element={<Settings />} />
        <Route path="messaging" element={<Navigate to="/voice-chat" replace />} />
        <Route path="gateways" element={<Navigate to="/voice-chat" replace />} />
        <Route path="bridges" element={<Navigate to="/voice-chat" replace />} />
        <Route path="voice-chat/trunk/new/:provider" element={<AddTrunk />} />
        <Route path="voice-chat/trunk/:id/edit" element={<AddTrunk />} />
        {/* /trunks retired — Voice & Chat is the single trunk page */}
        <Route path="trunks" element={<Navigate to="/voice-chat" replace />} />
        <Route path="stir-shaken" element={<StirShaken />} />
        <Route path="ring-groups" element={<RingGroups />} />
        <Route path="paging-groups" element={<PagingGroups />} />
        <Route path="health" element={<Health />} />
        <Route path="ring-groups/new" element={<RingGroupEditor />} />
        <Route path="ring-groups/:ring_group_uuid/edit" element={<RingGroupEditor />} />
        <Route path="queues/new" element={<QueueEditor />} />
        <Route path="queues/:queue_uuid/edit" element={<QueueEditor />} />
        <Route path="digital-receptionist/new" element={<DigitalReceptionistEditor />} />
        <Route path="digital-receptionist/:id/edit" element={<DigitalReceptionistEditor />} />
        <Route path="shared-voicemail" element={<SharedVoicemail />} />
        <Route path="hotdesk" element={<Hotdesk />} />
        <Route path="departments" element={<Departments />} />
        <Route path="groups" element={<Groups />} />
        <Route path="ai-agents" element={<AiAgents />} />
        <Route path="skill-routing" element={<SkillRouting />} />
        <Route path="outbound-rules" element={<OutboundRules />} />
        <Route path="outbound-rules-v2" element={<OutboundRulesV2 />} />
        <Route path="call-history" element={<CallLegs />} />
        <Route path="wakeup-calls" element={<WakeupCalls />} />
        <Route path="room-status" element={<RoomStatus />} />
        <Route path="ldap-sync" element={<LdapSync />} />
        <Route path="public-talk" element={<PublicTalk />} />
        <Route path="e911-locations" element={<E911Locations />} />
        <Route path="voicemail-ai" element={<VoicemailAI />} />
        <Route path="push-devices" element={<PushDevices />} />
        <Route path="firmware-history" element={<FirmwareHistory />} />
        <Route path="alerts" element={<AdminAlerts />} />
        <Route path="boss-secretary" element={<BossSecretary />} />
        <Route path="pickup-groups" element={<PickupGroups />} />
        <Route path="conference-rooms" element={<ConferenceRooms />} />
        <Route path="reports/*" element={<Reports />} />
        <Route path="voicemail" element={<Voicemail />} />
        <Route path="wallboard" element={<Wallboard />} />
        <Route path="audit" element={<Audit />} />
        <Route path="event-log" element={<EventLog />} />
        <Route path="integrations/*" element={<Integrations />} />
        <Route path="system/*" element={<SystemPages />} />
        <Route path="reseller/*" element={<Reseller />} />
        <Route path="apps" element={<Apps />} />
        <Route path="apps/beta-testers" element={<BetaTesters />} />
        <Route path="import" element={<Import3cx />} />
        <Route path="meet" element={<Meet />} />
        <Route path="callflow" element={<CallFlow />} />
        <Route path="templates" element={<PhoneTemplates />} />
        <Route path="callflow-designer" element={<CallFlowDesigner />} />
        <Route path="crm-template" element={<CrmTemplateEditor />} />
        <Route path="crm-writeback" element={<CrmWriteback />} />
        <Route path="dect-fxs" element={<DectFxs />} />
        <Route path="fax" element={<Fax />} />
        <Route path="inbound-rules" element={<InboundRules />} />
        <Route path="holidays" element={<Holidays />} />
        <Route path="service-principals" element={<ServicePrincipals />} />
        <Route path="emergency-geo" element={<EmergencyGeo />} />
        <Route path="trunk-templates" element={<TrunkTemplates />} />
        <Route path="activity-log" element={<ActivityLog />} />
        <Route path="blocklist" element={<Blocklist />} />
        <Route path="ip-allowlist" element={<IpAllowlist />} />
        <Route path="contacts" element={<Contacts />} />
        <Route path="api-keys" element={<ApiKeys />} />
        <Route path="sbc-onboarding" element={<SbcOnboarding />} />
        <Route path="auto-provisioning" element={<AutoProvisioning />} />
        <Route path="security" element={<CdrAnomaly />} />

        {/* Advanced — single wildcard route. The Advanced wrapper renders an
            internal rail and dispatches to the per-section panels. Direct URLs
            like /advanced/ip-blacklist still work since the wrapper handles them. */}
        <Route path="advanced/*" element={<Advanced />} />
        <Route path="system/license"             element={<LicensePage />} />
        <Route path="system/billing"             element={<Billing />} />
        <Route path="system/updates"             element={<UpdatesPage />} />
        <Route path="system/phonebook"           element={<Contacts />} />
        <Route path="system/multi-company"       element={<MultiCompany />} />
        <Route path="system/system-prompts"      element={<SystemPrompts />} />
        <Route path="system/remote-voicemail"    element={<RemoteVoicemail />} />

        {/* Top-level pages added in nav refactor */}
        <Route path="backup"                     element={<Backup />} />
        <Route path="office-hours"               element={<OfficeHours />} />
        <Route path="recordings"                 element={<CallRecordings />} />
        <Route path="system/firewall"            element={<Firewall />} />
        <Route path="system/services"            element={<Services />} />
        <Route path="system/theme"               element={<ThemeStyle />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
